import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, apiErrorMessage } from '@/lib/api'

type Consent = {
  id: string
  purpose: string
  granted: boolean
  grantedAt: string
  revokedAt: string | null
}

type DR = {
  id: string
  type: string
  status: string
  requestedAt: string
  completedAt: string | null
  downloadUrl: string | null
}

export default function PrivacyPage() {
  const qc = useQueryClient()

  const consents = useQuery({
    queryKey: ['me', 'consent'],
    queryFn: async () =>
      (await apiClient.get<{ data: Consent[] }>('/api/v1/me/consent')).data,
  })

  const requests = useQuery({
    queryKey: ['me', 'data-requests'],
    queryFn: async () =>
      (await apiClient.get<{ data: DR[] }>('/api/v1/me/data-requests')).data,
  })

  const consentMut = useMutation({
    mutationFn: async (vars: { purpose: string; granted: boolean }) => {
      await apiClient.post('/api/v1/me/consent', vars)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'consent'] }),
  })

  const exportMut = useMutation({
    mutationFn: async () => {
      return (await apiClient.post<{ id: string; downloadUrl: string }>('/api/v1/me/data-export')).data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['me', 'data-requests'] })
      const a = document.createElement('a')
      a.href = data.downloadUrl
      a.download = 'iznir-export.json'
      a.click()
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => {
      await apiClient.post('/api/v1/me/data-deletion')
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'data-requests'] }),
  })

  const purposes = ['analytics', 'marketing', 'third_party_sharing'] as const

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Privacy & data</h1>
        <p className="text-sm text-neutral-500">Consent, export, and deletion (GDPR).</p>
      </div>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-3">Consent</h2>
        <ul className="space-y-2 text-sm">
          {purposes.map((p) => {
            const rec = consents.data?.data.find((c) => c.purpose === p)
            const granted = rec?.granted ?? false
            return (
              <li key={p} className="flex items-center justify-between">
                <span className="font-mono text-xs">{p}</span>
                <button
                  onClick={() => consentMut.mutate({ purpose: p, granted: !granted })}
                  disabled={consentMut.isPending}
                  className={
                    granted
                      ? 'text-xs rounded bg-green-600 text-white px-2 py-1'
                      : 'text-xs rounded border px-2 py-1'
                  }
                >
                  {granted ? 'granted' : 'grant'}
                </button>
              </li>
            )
          })}
        </ul>
      </section>

      <section className="rounded border bg-white p-4 space-y-3">
        <h2 className="font-semibold">Your data</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => exportMut.mutate()}
            disabled={exportMut.isPending}
            className="text-sm rounded bg-neutral-900 text-white px-3 py-1.5 disabled:opacity-50"
          >
            {exportMut.isPending ? 'Preparing…' : 'Export my data'}
          </button>
          <button
            onClick={() => {
              if (confirm('File a deletion request? You can cancel within 30 days.')) {
                deleteMut.mutate()
              }
            }}
            disabled={deleteMut.isPending}
            className="text-sm rounded border border-red-600 text-red-700 px-3 py-1.5 disabled:opacity-50"
          >
            {deleteMut.isPending ? 'Filing…' : 'Request deletion'}
          </button>
        </div>
        {exportMut.error && (
          <p className="text-xs text-red-600">{apiErrorMessage(exportMut.error)}</p>
        )}
        {deleteMut.error && (
          <p className="text-xs text-red-600">{apiErrorMessage(deleteMut.error)}</p>
        )}
      </section>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-3">Request history</h2>
        {(requests.data?.data ?? []).length === 0 ? (
          <p className="text-sm text-neutral-500">No requests yet.</p>
        ) : (
          <ul className="divide-y text-sm">
            {(requests.data?.data ?? []).map((r) => (
              <li key={r.id} className="py-2 flex items-center justify-between">
                <span>
                  <span className="font-mono text-xs">{r.type}</span>
                  <span className="text-xs text-neutral-500 ml-2">{r.status}</span>
                </span>
                <span className="text-xs text-neutral-500">
                  {new Date(r.requestedAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
